<template>
  <VueFinalModal
    v-model="show"
    classes="overflow-y-auto bg-black/50"
    content-class="flex flex-wrap justify-center box-border items-center w-full h-full box-border overflow-y-auto p-4"
    esc-to-close
    click-to-close
    @closed="close"
  >
    <div
      v-if="['initial', 'finish'].includes(status)"
      :class="{
        'w-full max-w-[1240px] sm:min-h-[680px]': type !== 'img',
        'w-auto': type === 'img'
      }"
      class="container"
    >
      <div class="mb-4 flex items-center">
        <div v-if="title" class="font-semibold text-lg">
          {{ title }}
        </div>
        <div v-else class="font-semibold text-lg">
          {{ $t('labels.preview') }}
          {{ { link: $t('labels.link'), video: $t('labels.video'), pdf: $t('labels.pdf'), img: $t('labels.image'), scorm: $t('labels.link') }[type] || $t('labels.content') }}
        </div>
        <div class="grow"></div>
        <div v-if="allowDownload && type.includes('document')" class="mr-6">
          <UiButton type="primary" outline icon="download" icon-size="18px" class="p-1.5 text-sm outline-[1px]" @click="downloadFile">
            {{ $t('button.download') }}
          </UiButton>
        </div>

        <ReactButton
          v-if="route.name !== 'files'"
          class="mx-2"
          btnClass="h-12 w-12"
          typeContent="midia"
          :contentId="mediaInfo?.id"
          :reaction="mediaInfo?.reacao"
          :qtd="mediaInfo?.totalReacoes"
          @onSuccess="onReacted"
        />

        <UiButton type="ghost" round icon="x" class="p-2" @click="close" />
      </div>

      <UiAlert
        v-if="type === 'video' && media?.videoInfo?.status !== 4"
        type="info"
        :title="$t('feedback.processingMediaTitle')"
        :text="$t('feedback.processingMediaText')"
        class="flex self-center m-auto w-fit"
      />

      <Player
        v-else-if="src"
        :source="(media?.hlsPlaylistURL ? `${media?.pullzone}/${media?.videoId}/playlist.m3u8` : src) || ''"
        :type="type"
        :mediaId="mediaId"
        :pullzone="media?.pullzone"
        :progress="progress"
        :video-id="media?.videoId"
        :allow-download="mediaInfo?.permitirDownload || false"
        :watermark="objData?.watermarkRules.enabled"
        :watermark-rules="objData?.watermarkRules"
        :disableFastForward="media?.bloquearAvanco"
        :slides="slides"
        :freeContent="type === 'freeContent' ? media?.freeContent : ''"
        :video-info="media?.videoInfo"
        @on-load-frame="loadScormPlayer"
        @on-update-progress="registerProgress"
        class="w-full h-auto aspect-video"
      />
    </div>
    <div v-if="status === 'loading'" class="container w-auto h-auto flex flex-col gap-2 p-4 items-center justify-center">
      <UiIcon icon="loader" class="animate-spin mb-2" />
      <div>{{ $t('feedback.loading') }}</div>
    </div>
    <div v-if="status === 'error'" class="container">
      <UiAlert
        type="danger"
        :title="$t('alerts.errorMediaTitle')"
        :text="$t('alerts.errorMediaMessage')"
        class="mb-6"
      />

      <UiButton type="primary" outline class="px-4 py-2 text-sm uppercase outline-[1px]" @click="close">
        {{ $t('button.close') }}
      </UiButton>
    </div>
    <a ref="downloadBtn" href="" download></a>
  </VueFinalModal>
</template>

<script setup lang="ts">
import { ref, reactive, nextTick, watch } from 'vue'
import { VueFinalModal } from 'vue-final-modal'
import { useRoute } from 'vue-router'
import { useAuthStore, useSalaDeAulaStore, useSecoesStore } from '@/stores'
import midiasService from '@/services/midias.service'
import { midiaService } from '@/services/midias.service'
import scormAcompanhamento from '@/services/scormAcompanhamento.service'

import UiIcon from '@/components/ui/icon.vue'
import UiAlert from '@/components/ui/alert.vue'
import UiButton from '@/components/ui/button.vue'
import Player from './player.vue'
import ReactButton from '@/views/learningCenter/classRoom/components/reactButton.vue'

import type { MidiaType, ScormItem, ViewMediaType } from '@/types/types'

defineOptions({
  name: 'ModalPlantarPlayer'
})

const props = defineProps<ViewMediaType>()

const route = useRoute()
const salaDeAulaStore = useSalaDeAulaStore()
const emit = defineEmits(['onClose', 'onRegisterProgress'])

const defaultScormData = {
  id: '',
  userId: '',
  inscricaoId: '',
  midiaId: '',
  children: '',
  version: '',
  completionStatus: '',
  completionThreshold: '',
  credit: '',
  entry: '',
  exit: '',
  launchData: '',
  location: '',
  maxTimeAllowed: '',
  mode: '',
  progressMeasure: 0,
  scaledPassingScore: 0,
  scoreChildren: 0,
  scoreScaled: 0,
  scoreRaw: 0,
  scoreMin: 0,
  scoreMax: 0,
  sessionTime: '',
  successStatus: '',
  suspendData: '',
  timeLimitAction: '',
  totalTime: '',
  adlNavRequest: '',
  fullTime: '',
  suspendData1: '',
  scormScriptLink: ''
}

const downloadBtn = ref()
const scormData = reactive<ScormItem>({...defaultScormData})
const listenOnMessage = ref(false)
const pdfViewerRef = ref()
const media = ref<MidiaType>()
const mediaInfo = ref<MidiaType>()
const status = ref<'initial' | 'loading' | 'error' | 'finish'>('initial')
const show = ref(false)
const localProgress = ref(0)
const slides = ref<string[]>([])

watch(
  () => [pdfViewerRef.value?.page, pdfViewerRef.value?.pageCount],
  (nVal, oVal) => {
    if (!props.trackProgress) return

    if (!oVal[1] && !!nVal[1] && nVal[1] > 1 && props.progress > 0 && props.progress < 100) {
      pdfViewerRef.value?.jumpToPercent(props.progress)
    }
    if (pdfViewerRef.value?.pageCount) {
      const percent = Math.round((pdfViewerRef.value.page / pdfViewerRef.value.pageCount) * 100)
      percent > localProgress.value && registerProgress(percent)
    }
  }
)

const downloadFile = () => {
  downloadBtn.value.href = props.src
  downloadBtn.value.click()
}

const loadFreeContent = async () => {
  if (!props.mediaId) return
  status.value = 'loading'

  try {
    const { data } = await midiaService.getFileInfo(props.mediaId)
    media.value = data
    status.value = 'finish'
  } catch {
    status.value = 'error'
  }
}

const open = async () => {
  show.value = true

  nextTick(async () => {
    if (['video', 'audio'].includes(props.type)) {
      await loadInfoMedia()
    }
    else if (props.type === 'scorm' && props.trackProgress) {
      window.addEventListener('message', onMessage, false)
      await loadProgressScorm()
      props?.src?.includes('scormcontent') && props.trackProgress && registerProgress(100)
    }
    else if (['img', 'document', 'documento'].includes(props.type) && props.trackProgress) {
      registerProgress(100)
    }
    else if (props.type === 'freeContent') {
      await loadFreeContent()
    }

    localProgress.value = props.progress

    loadMediasInfo()
  })
}

const close = () => {
  props.type === 'scorm' && props.trackProgress && window.removeEventListener('message', onMessage, false)
  Object.assign(scormData, {...defaultScormData})
  localProgress.value = 0
  show.value = false
  status.value = 'initial'
  emit('onClose')
}

const registerProgress = (progress: number) => {
  event?.stopPropagation()

  const fmProgress = Math.round(progress)

  if ((fmProgress < localProgress.value) || fmProgress === 0) return

  localProgress.value = fmProgress

  if (!props.trackProgress || props.progress === 100) return

  midiasService.registerProgress(props.mediaId, fmProgress, {
    inscriptionId: null,
    isClassroom: false
  }).then(() => emit('onRegisterProgress', {progress: fmProgress, id: props.mediaId}))
}

const loadScormPlayer = async () => {
  scormData.userId = useAuthStore().persistentInfo.id

  if (props.trackProgress) {
    await salaDeAulaStore.getInfoScorm(props.mediaId, {
      inscricaoId: ['formation', 'trail', 'course'].includes(salaDeAulaStore?.type) ? salaDeAulaStore.info.inscription.id : null
    })

    Object.assign(scormData, { ...salaDeAulaStore.scormProgress })
  }

  setTimeout(() => {
    const user = useAuthStore().persistentInfo
    sendToScormPlayer('start', salaDeAulaStore.scormProgress.suspendData, {
      name: user?.fullName || '',
      nick: user?.name !== user?.fullName ? user?.name : '',
      role: user?.role || '',
      department: user?.department || '',
      unit: user?.unidade?.name || ''
    })
    listenOnMessage.value = true
  }, 1000)
}

const sendToScormPlayer = (func: string, data?: any, user?: any) => {
  nextTick(() => {
    const scormFrame = (document.getElementById('player-frame') as any)?.contentWindow
    if (scormFrame) scormFrame.postMessage({ func, data: data || null, user: user || null }, '*')
  })
}

// Transforma o nome das propriedades scorm em camelCase
// cmi.core.lesson_status_exit => lessonStatusExit
const transformCamelCaseScorm = (obj: any) => {
  return obj
    .split('.')
    .at(-1)
    .split('_')
    .map((v, i) => (i === 0 ? v : v[0].toUpperCase() + v.substring(1)))
    .join('')
}

const onMessage = (event) => {
  const { data: msgData } = event
  if (!msgData || !listenOnMessage.value) return

  const { func, data } = msgData
  if (func === 'LMSSetValue') {
    const prop = transformCamelCaseScorm(data.item)
    scormData[prop] = data.value

    if (prop === 'lessonStatus' && ['completed', 'passed'].includes(data.value)) registerProgress(100)
  }
  if (func === 'LMSCommit') {
    scormAcompanhamento.put('', scormData)
  }
  else if (func === 'riseCommit') {
    scormData.suspendData = data.suspend_data

    scormAcompanhamento.put('', {
      ...data,
      userId: scormData.userId,
      id: scormData.id
    })

    if (['completed', 'passed'].includes(data.lesson_status)) registerProgress(100)
  }
}

const loadInfoMedia = async () => {
  if (!props.mediaId) return
  status.value = 'loading'

  try {
    const { data } = props.accessDetail
      ? await midiaService.getDetalhes(props.mediaId, props.ignoreFilterSection)
      : await midiasService.get(props.mediaId)

    if (props.type === 'video') {
      const { data: videoData } = await midiaService.getVideoInfo(data.videoId) 

      media.value = {
        ...data,
        videoInfo: videoData
      }
    } else {
      media.value = data
    }

    status.value = 'finish'
  } catch {
    status.value = 'error'
  }
}


const loadProgressScorm = async () => {
  if (!props.mediaId) return

  status.value = 'loading'

  try {
    const { data } = await scormAcompanhamento.getProgressScorm(props.mediaId)
    if (!data) {
      const { data: progressCreated, success } = await scormAcompanhamento.post('', { midiaId: props.mediaId })
      if (success) {
        Object.assign(scormData, progressCreated)
      }
    } else Object.assign(scormData, data)

    status.value = 'finish'
  } catch {
    status.value = 'error'
  }
}

const onReacted = (res: any) => {
  Object.assign(mediaInfo.value, {
    ...mediaInfo.value,
    reacao: res?.reacao,
    totalReacoes: res?.totalReacoes
  })

  useSecoesStore().updateSectionItem(mediaInfo.value?.id, {
    totalReacao: res?.totalReacoes,
    reacao: res?.reacao
  })
}

const loadMediasInfo = async () => {
  if (!props.mediaId || route.name === 'files') return

  if (['video', 'audio'].includes(props.type)) return mediaInfo.value = media.value

  const { data } = props.accessDetail
    ? await midiaService.getDetalhes(props.mediaId, props.ignoreFilterSection)
    : await midiasService.get(props.mediaId)

  mediaInfo.value = data

  if (data?.carrossel) {
    slides.value = data.carrossel.map((slide: any) => slide.link)
  }
}

defineExpose({
  open,
  close
})
</script>

<style scoped>
.container {
  @apply text-center py-4 rounded-lg bg-neutral-100 p-2 text-neutral-800 dark:bg-neutral-700 dark:text-neutral-100;
}
</style>
